import {
  Button,
  Form,
  Pagination,
  Space,
  Table,
  Tooltip,
  Tag,
  Modal,
  Typography,
  notification,
} from 'antd';
import { DownloadOutlined, EditOutlined, FilePdfOutlined } from '@ant-design/icons';
import CustomButton from 'components/custom-button';
import ExportTimesheetModal from 'components/export-timesheet-modal';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import PageTitle from '../../components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import Filter, {
  PAYSLIP_STATUS_COLOR,
  PAYSLIP_STATUS_LABEL,
  PayslipFilterFormValue,
} from './component/filter';
import ModalUpdatePayslip from './component/ModalUpdatePayslip';
import moment from 'moment';
import { formatData } from 'utils/common';
import {
  LinkedFilterTag,
  toPositiveInt,
  useDropSearchParams,
  useParamChange,
} from 'pages/user-manager/helpers/user-deep-link';

const LIMIT = 10;

const initFilter = {
  page: 1,
  limit: LIMIT,
  month: moment().format('YYYY-MM'),
};

export interface PayslipFilter {
  limit: number;
  page: number;
  month?: string;
  userId?: number;
  username?: string;
}

const formatter = new Intl.NumberFormat('vi-VN');

/**
 * Liên kết sâu: ?userId=<id>&name=<tên> và/hoặc ?month=YYYY-MM.
 * Lọc theo một người mà không kèm month → mọi tháng (phiếu tháng này có thể chưa tạo).
 */
function readDeepLink(params: URLSearchParams) {
  const userId = toPositiveInt(params.get('userId'));
  const rawMonth = params.get('month');
  const month = rawMonth && /^\d{4}-(0[1-9]|1[0-2])$/.test(rawMonth) ? rawMonth : undefined;
  if (!userId && !month) return null;
  const filter: PayslipFilter = formatData({ page: 1, limit: LIMIT, userId, month });
  const preset: Partial<PayslipFilterFormValue> = { month: month ? dayjs(`${month}-01`) : null };
  return { filter, preset };
}

const PayslipsManager = () => {
  const [timesheetOpen, setTimesheetOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [searchParams] = useSearchParams();
  const dropSearchParams = useDropSearchParams();
  const [deepLink] = useState(() => readDeepLink(searchParams));
  const [filterForm] = Form.useForm<PayslipFilterFormValue>();
  const linkedUserId = toPositiveInt(searchParams.get('userId'));
  const linkedUserLabel = searchParams.get('name') || `#${linkedUserId}`;
  const [filter, setFilter] = useState<PayslipFilter>(deepLink?.filter || initFilter);
  const [isOpenModalUpdate, setIsOpenModalUpdate] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [valueSelected, setValueSelected] = useState<any>(null);
  const [isOpenFinalize, setIsOpenFinalize] = useState(false);
  const [isFinalizeLoading, setIsFinalizeLoading] = useState(false);

  const onFinish = (value: Partial<PayslipFilter>) => {
    setFilter((preState) => formatData({ ...preState, ...value, page: 1 }));
    // "Xoá lọc" gửi userId: undefined → bỏ luôn người đang lọc trên URL.
    if ('userId' in value && linkedUserId) dropSearchParams('userId', 'name');
  };

  // URL đổi sau khi trang đã mở (đóng thẻ "Đang lọc", bấm lại menu, Back/Forward).
  useParamChange(linkedUserId, (next, prev) => {
    setFilter((p) => {
      if (next) return { ...p, userId: next, page: 1 };
      return p.userId === prev ? formatData({ ...p, userId: undefined, page: 1 }) : p;
    });
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.payslips, {
          ...filter,
        });
        const items = res?.data?.data || res?.data?.items || res?.data || [];
        // GET /payslips trả `meta` (không phải `metadata` như các API khác) — đọc cả hai.
        const total =
          res?.data?.meta?.total || res?.data?.metadata?.total || res?.data?.totalCount || 0;
        setDataResponse({ items, totalCount: total });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filter, statusUpdateData]);

  const columns: ColumnsType<any> = [
    {
      title: 'Mã phiếu',
      dataIndex: 'code',
      key: 'code',
      // Bảng có scroll.x + cột cố định → table-layout: fixed: cột phải có width, mã/tiền giữ một dòng.
      // 186 vừa mã dài nhất hiện có ("PS-202608-HP0030"); mã dài hơn thì cắt "…", rê chuột xem đủ.
      width: 186,
      render: (value) => <Typography.Text ellipsis={{ tooltip: value }}>{value}</Typography.Text>,
    },
    {
      title: 'Tháng',
      dataIndex: 'month',
      key: 'month',
      width: 96,
      render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{value}</span>,
    },
    {
      title: 'Nhân viên',
      key: 'username',
      width: 156,
      render: (record) => (
        <Tooltip title={record?.user?.email ?? ''}>
          <span>{record?.user?.nickName || record?.user?.username || ''}</span>
          {record?.user?.nickName && record?.user?.username ? (
            <div style={{ fontSize: 12, opacity: 0.65 }}>{record.user.username}</div>
          ) : null}
        </Tooltip>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value: string) => (
        <Tag
          color={PAYSLIP_STATUS_COLOR[value] || 'default'}
          style={{ borderRadius: 6, padding: '2px 8px' }}
        >
          {PAYSLIP_STATUS_LABEL[value] || value}
        </Tag>
      ),
    },
    {
      title: 'Tổng thu nhập',
      dataIndex: 'grossIncome',
      key: 'grossIncome',
      width: 130,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {typeof value === 'number' ? formatter.format(value) : ''}
        </span>
      ),
    },
    {
      title: 'Khấu trừ',
      dataIndex: 'totalDeduction',
      key: 'totalDeduction',
      width: 110,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {typeof value === 'number' ? formatter.format(value) : ''}
        </span>
      ),
    },
    {
      title: 'Thực nhận',
      dataIndex: 'netPay',
      key: 'netPay',
      width: 120,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {typeof value === 'number' ? formatter.format(value) : ''}
        </span>
      ),
    },
    {
      title: 'File PDF',
      dataIndex: 'pdfUrl',
      key: 'pdfUrl',
      width: 92,
      render: (value) =>
        value ? (
          <a href={value} target="_blank" rel="noreferrer" style={{ whiteSpace: 'nowrap' }}>
            Xem PDF
          </a>
        ) : (
          <span />
        ),
    },
    {
      title: 'Thao tác',
      // Hai nút icon 36px + khoảng cách 8 + đệm ô 2×16.
      width: 112,
      key: 'action',
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Sửa"
              onClick={() => {
                setValueSelected(record);
                setIsOpenModalUpdate(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Chốt & tạo PDF">
            <Button
              type="text"
              icon={<FilePdfOutlined />}
              aria-label="Chốt và tạo PDF"
              onClick={() => {
                setValueSelected(record);
                setIsOpenFinalize(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  return (
    <div>
      <PageTitle
        title="Phiếu lương"
        extra={
          <CustomButton
            bgColor="primary-green"
            text="Xuất bảng công & lương"
            icon={<DownloadOutlined />}
            onClick={() => setTimesheetOpen(true)}
          />
        }
      />
      <div style={{ marginBottom: 24 }}>
        <Filter form={filterForm} preset={deepLink?.preset} onSubmit={onFinish} />
      </div>
      {linkedUserId && filter.userId === linkedUserId && (
        <LinkedFilterTag
          label={linkedUserLabel}
          onClose={() => dropSearchParams('userId', 'name')}
        />
      )}
      <div className="custom-table custom-form">
        <Table
          columns={columns}
          rowKey={(record) => record.id || record.code}
          pagination={false}
          dataSource={dataResponse.items}
          // Tổng độ rộng cột = 1112: vừa khung bảng ở 1440 (1118), màn nhỏ hơn thì cuộn ngang trong thẻ.
          scroll={{ x: 1112 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <Pagination
            showSizeChanger={false}
            defaultCurrent={LIMIT}
            current={filter.page}
            onChange={handleChangePage}
            showLessItems
            total={dataResponse.totalCount}
            pageSize={LIMIT}
          />
        )}
      </div>
      <ExportTimesheetModal open={timesheetOpen} onClose={() => setTimesheetOpen(false)} />
      {isOpenModalUpdate && (
        <ModalUpdatePayslip
          isOpen={isOpenModalUpdate}
          handleCancel={() => setIsOpenModalUpdate(false)}
          onUpdated={() => setStatusUpdateData(!statusUpdateData)}
          valueSelected={valueSelected}
        />
      )}
      {isOpenFinalize && (
        <Modal
          title="Chốt phiếu lương & tạo PDF"
          open={isOpenFinalize}
          confirmLoading={isFinalizeLoading}
          onOk={async () => {
            setIsFinalizeLoading(true);
            try {
              await AxiosUtils.post(`${API.payslips}/${valueSelected?.id}/finalize`, {});
              notification.success({ message: 'Tạo PDF thành công' });
              setIsOpenFinalize(false);
              setStatusUpdateData(!statusUpdateData);
            } catch (e: any) {
              notification.error({ message: e?.message || 'Tạo PDF thất bại' });
            } finally {
              setIsFinalizeLoading(false);
            }
          }}
          onCancel={() => setIsOpenFinalize(false)}
        >
          <p>
            Chốt phiếu lương {valueSelected?.code ? <strong>{valueSelected.code}</strong> : null} và
            tạo file PDF? Sau khi chốt, trạng thái chuyển sang “Đã chốt”.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default PayslipsManager;
